'use client';

import Image from 'next/image';
import { useRouter } from '@/navigation';
import { useLocale, useTranslations } from 'next-intl';
import { ImageCouldFromRemoteServer as RemoteServerImage } from '@/components/images'
import type { NavItems } from '@/types/nav';
import { TaskTypeEnum } from '@/domains/wc-promotion/types';
import { Maybe, matchEquals } from '@nf/utils-common/fpw/monadic';
import { sportNewTagMap, sportPickTagMap } from '@/settings/sports/sport-new-tag-map';
import { uiService } from '@/services/ui.service';
import { Icon, SportIcon, NewWindowIcon, FilterLiveIcon } from '@/components/icons/svg-icons';
import { SimpleAccordionDetails, Button } from '@nf/ui';
import { Tip } from './tip';
import { setKafkaNormal } from '@/hooks/query/use-kafka-log';
import { menuIconStyle } from './sidebar-item.style';
import { useAppearanceStore } from '@/store/appearance';
import { ViewEnum } from '@/types';
import { useSiteStore } from '@/store/site';
import { useAuthToken } from '@/store/auth';
import { useGetSiteConfigs } from '@/hooks/query/use-site-configs';
import { useAccountInfo } from '@/hooks/query/account';
import { useCasinoRoyale } from '@/hooks/query/use-casino-royale';
import { KafkaActionSettings, KafkaMarsTypeEnums, KafkaNote } from '@nf/utils-common/constants';
import { sportService } from '@/services/sport.service';
import { useSelectedLayoutSegments } from 'next/navigation';
import { openESport } from '@/services/third-party-entrance.service';
import { openExchangeInNewWindow } from '@/hooks/query';
import { AnalyseKey, useAnalyseAttribute, useConfigsAnalyseAttribute } from '@/features/next-analyse';
import { NewFeatureBadge } from '@/components/new-tag/new-feature-badge';
import { useAtomValue } from 'jotai';
import { visitorAtom } from '@/atoms/visitor';
import clsx from 'clsx';
import { useEffect, useMemo, useState } from 'react';
import { Tooltip } from '@/components/widgets/tooltip';
import { wcPromotionMissionService } from '@/domains/wc-promotion/services/wc-promotion-mission.service';
import { modalService } from '@/services/modal.service';
import { goldenDuelStorageService } from '@/services/golden-duel/golden-duel-storage.service';
import { useOddsPageSwitcher } from '@/hooks/use-odds-page-switcher';
import { useLicSettings } from '@/domains/config/hooks/use-lic-settings';
import { usePredictionMarket } from '@/hooks/use-prediction-market';
import { SPORT_TYPE_TO_CLICK_TASK_TYPE } from '@/domains/wc-promotion/hooks/use-wc-promotion-mission-tracker';
import { sidebarStyle } from '../../sidebar.style';

interface SidebarItemProps {
	item: NavItems[0];
	clickThenNavigate?: boolean;
	children: React.ReactNode;
}

const nextAnalyseHrefKeyMapping = {
	sports: 'switch-to-odds-page-via-left-menu',
	world: 'switch-to-odds-page-via-left-menu',
	live: 'switch-to-odds-page-via-left-menu',
	bets: 'switch-to-statement-via-left-menu',
	lobby: 'switch-to-lobby-via-left-menu',
	promotion: 'switch-to-promotion-via-left-menu',
	favorites: 'switch-to-my-favorites-via-left-menu'
};

const getNextAnalyseKey = (itemHref: string): string => {
	return Object.entries(nextAnalyseHrefKeyMapping).reduce((analyseKey, mapping): string => {
		const [href, key] = mapping;
		if (!analyseKey && itemHref.startsWith(`/${href}`)) {
			analyseKey = key;
		}
		return analyseKey;
	}, '');
};

/**
 * sports that need to be analyzed by catcher
 */
const analyseSports = ['1', '2', '5', '6', '8', '43', '50', '179'];

export const SidebarItem = ({
	item,
	clickThenNavigate,
	children
}: SidebarItemProps) => {
	const router = useRouter();
	const locale = useLocale();
	const hasSubItems = Boolean(item.subItems?.length);
	const { disableParlay } = useOddsPageSwitcher();
	const [
		isParlayMode,
		deepLinkSiteInfo
	] = useSiteStore(state => [
		state.isParlayMode,
		state.deepLinkSiteInfo
	]);
	const view = useAppearanceStore(state => state.view);
	const [resetAllFilter] = useSiteStore(state => [state.resetAllFilter]);
	const [setCurrentPage] = useSiteStore(state => [state.setCurrentPage]);
	const { resetSwitcher } = useOddsPageSwitcher();
	const authToken = useAuthToken();
	const isBefore = !authToken;
	const visitorInfo = useAtomValue(visitorAtom);
	const beforeToken = visitorInfo?.AccessToken;
	const { SiteConfigsData } = useGetSiteConfigs();
	const { user } = useAccountInfo();
	const { openPredictionMarket } = usePredictionMarket();
	const { openCasinoRoyale } = useCasinoRoyale();
	const t = useTranslations('General');
	const selectedLayoutSegments = useSelectedLayoutSegments();
	const pathSegments = selectedLayoutSegments.map(segment => `/${segment}`);
	const { nonSport } = useLicSettings();
	
	const getNewTagConfigForSport = (item: NavItems[0]) => {
		if (item.sportId === undefined) return item.newTagUntil ? { to: item.newTagUntil } : undefined;
		const numericSportId = Number(item.sportId);
		const shouldShow = sportService.shouldShowNewTagForKironSport(numericSportId, nonSport?.virtualSoccerList);
		const newTagId = sportService.getKironSportNewTagId(numericSportId, nonSport?.virtualSoccerList);
		return shouldShow ? sportNewTagMap[newTagId] : undefined;
	};
	
	const [isOpen, setIsOpen] = useState<boolean>(false);
	let nextAnalyseKey = getNextAnalyseKey(item.href);

	if (item.href.startsWith('/sports/') && analyseSports.includes(item.sportId ?? '')) {
		nextAnalyseKey = `${nextAnalyseKey}-${item.sportId}`;
	}

	const isSport112 = String(item.sportId) === '112' && !hasSubItems;
	const sidebarItemAnalyseConfigs = useMemo(() => {
		if (isSport112) {
			return [{ key: 'switch-to-odds-page-via-left-menu-112', analyseKey: AnalyseKey.Refined }];
		}
		if (!nextAnalyseKey) {
			return [];
		}
		return [{ key: nextAnalyseKey, analyseKey: AnalyseKey.Base }];
	}, [isSport112, nextAnalyseKey]);
	const taggedElement = useConfigsAnalyseAttribute(sidebarItemAnalyseConfigs);
	const [needShowGoldenDuelTooltip, setNeedShowGoldenDuelTooltip] = useState(true);
	const analyseStreamersRef = useAnalyseAttribute('open-streamers-list-button-from-menu');
	const itemPickTagConfig = item.href === '/promotion' && (user?.EnableSBRWidget || isBefore)
		? sportPickTagMap.sbr
		: undefined;
	const itemNewTagConfig = getNewTagConfigForSport(item);
	const setKafkaGalaxy = () => {
		if (item.href === '/third-party/prediction-market') {
			setKafkaNormal({ note: KafkaNote.PredictionMarket, type: 1, action: 6 });
			return;
		}
		if (item.href.includes('/world/world-cup')) {
			setKafkaNormal({ note: KafkaNote.GalaxyV2, type: 4, action: 15 });
			return;
		}
		if (item.href.includes('saba-virtual-fifa-world-cup')) {
			setKafkaNormal({ note: KafkaNote.GalaxyV2, type: 4, action: 16 });
			return;
		} 
		if (item.href.includes('fun88-virtual-fifa-world-cup')) {
			setKafkaNormal({ note: KafkaNote.GalaxyV2, type: 4, action: 17 });
			return;
		}
		if (item.href.includes('m88-virtual-fifa-world-cup')) {
			setKafkaNormal({ note: KafkaNote.GalaxyV2, type: 4, action: 18 });
			return;
		}
		if (item.href.includes('streamer')) {
			const isMobileMode = window.innerWidth < 750;
			setKafkaNormal({
				note: KafkaNote.GalaxyV2,
				type: 13,
				action: isMobileMode ? 6 : 9,
			});
			return;
		}

		if (item.href.includes('sport-event')) {
			setKafkaNormal({
				note: KafkaNote.KingBack,
				type: 1,
				action: 6
			});
		}

		if (item.sportId === '993' || (item.sportId === '134' && item.menuSource === 'sports')) {
			const kafkaData = {
				note: KafkaNote.Betby,
				type: 2,
				action: item.sportId === '993' ? 10 : 11
			}
			setKafkaNormal(kafkaData);
		}

		if (sportService.isAnyVirtualSportsProduct(Number(item.sportId))) {
			if (clickThenNavigate !== false) {
				if (item.sportId === '116') {
					setKafkaNormal({
						note: KafkaNote.Betby_eSoccer,
						type: 2,
						action: 3,
					})
				}
				if (item.sportId === '134' && item.menuSource !== 'sports') {
					setKafkaNormal({
						note: KafkaNote.Betby,
						type: 2,
						action: 3,
					})
				}
			}
			if (hasSubItems) {
				setKafkaNormal({
					note: KafkaNote.GalaxyV2,
					type: 15,
					action: 1
				});
			}
			return;
		}

		if (item.sportId === '1') {
			setKafkaNormal({ note: KafkaNote.GalaxyV2, type: 4, action: 14 });
		}

		let action = {
			'/lobby': 11, // home
			'/promotion': 12, // promotion
			'/bets/u': 2, // my bets
			'/search': 3, // serach
			'/favorites': 4, // my fav
			'/section/ph': 13, // PH
		}[item.href];

		if (!action) {
			if (item.href.includes('olympics')) {
				action = 5;
			} else if (item.href.includes('euro-cup') || item.href.includes('world/womens-euro') || item.href.includes('nations-league')) {
				action = 6;
			} else if (item.href.includes('copa-america')) {
				action = 7;
			} else if (item.href.includes('asian-cup')) {
				action = 8;
			}
		}

		if (action) {
			const kafkaData = {
				note: KafkaNote.GalaxyV2,
				type: 4,
				action
			}
			setKafkaNormal(kafkaData);
		}
	}

	const setKafkaMars = () => {
		if (item.href === '/third-party/prediction-market') {
			setKafkaNormal({ note: KafkaNote.PredictionMarket, type: 1, action: 6 });
			return;
		}
		if (item.href.includes('/world/world-cup')) {
			setKafkaNormal({ note: KafkaNote.MarsV1, type: 4, action: 26 });
			return;
		}
		if (item.href.includes('saba-virtual-fifa-world-cup')) {
			setKafkaNormal({ note: KafkaNote.MarsV1, type: 4, action: 27 });
			return;
		}
		if (item.href.includes('fun88-virtual-fifa-world-cup')) {
			setKafkaNormal({ note: KafkaNote.MarsV1, type: 4, action: 28 });
			return;
		}
		if (item.href.includes('m88-virtual-fifa-world-cup')) {
			setKafkaNormal({ note: KafkaNote.MarsV1, type: 4, action: 29 });
			return;
		}
		const isMobileMode = window.innerWidth < 750;

		const action = {
			'/lobby': isMobileMode ? 6 : 14, // home
			'/promotion': isMobileMode ? 7 : 15, // promotion
			'/favorites': isMobileMode ? 8 : 16, // my fav
			'/bets/u': isMobileMode ? 9 : 17, // statement
			'/streamer': isMobileMode ? 6 : 10 // streamer
		}[item.href];
		if (item.sportId === '1') {
			setKafkaNormal({ note: KafkaNote.MarsV1, type: 4, action: 24 });
		} else if (isMobileMode && item.sportId === '2') {
			setKafkaNormal({ note: KafkaNote.MarsV1, type: 4, action: 25 });
		}
		if (!isParlayMode && (item.sportId === '993' || item.sportId === '134' && item.menuSource === 'sports')) {
			const kafkaData = {
				note: KafkaNote.Betby,
				type: 1,
				action: item.sportId === '993' ? 10 : 11
			}
			setKafkaNormal(kafkaData);
		}
		if (clickThenNavigate !== false) {
			if (item.sportId === '116') {
				setKafkaNormal({
					note: KafkaNote.Betby_eSoccer,
					type: 1,
					action: 3,
				})
			}
			if (item.sportId === '134' && item.menuSource !== 'sports') {
				setKafkaNormal({
					note: KafkaNote.Betby,
					type: 1,
					action: 3,
				})
			}
		}
		
		if (action) {
			const isStreamer = item.href.includes('streamer');
			const kafkaData = {
				note: KafkaNote.MarsV1,
				type: isStreamer ? 5 : 4,
				action
			}
			setKafkaNormal(kafkaData);
		}
	}

	const setKafkaMarsHome = () => {
		const isHomePage = pathSegments.includes('/lobby');
		const isMobileMode = window.innerWidth < 750;
		if (!isHomePage || isMobileMode) return; // 只計 mars home page 且寬度 750 以上

		let action = {
			'/lobby': 16, // home
			'/promotion': 17, // promotion
			'/favorites': 18, // my fav
			'/bets/u': 19, // statement
		}[item.href];

		if (!action) {
			if (item.href.includes('euro-cup') && item.sportId === '1') {
				action = 5; // euro-cup > soccer
			} else if (item.href.includes('/sports') && item.sportId === '1' && !isParlayMode) {
				action = 22; // sports > soccer
			}
		}

		if (action) {
			const kafkaData = {
				note: KafkaNote.MarsV1,
				type: 1,
				action
			}
			setKafkaNormal(kafkaData);
		}
	}

	const setKafkaMarsPinGoal = () => {
		const pinGoalActions = KafkaActionSettings.MarsV1[KafkaMarsTypeEnums.PinGoal];
		const action = {
			'9995': pinGoalActions.PinGoalSoccer,
			'9996': pinGoalActions.PinGoalBasketball,
			'9997': pinGoalActions.PinGoalEsport,
		}[item.sportId ?? ''];
		if (action) {
			const kafkaData = {
				note: KafkaNote.MarsV1,
				type: KafkaMarsTypeEnums.PinGoal,
				action
			}
			setKafkaNormal(kafkaData);
		}
	}

	const setKafkaTournamentWidget = () => {
		const isClubWorldCup = item.href.includes('widget/club-world-cup');
		const isWomensEuro = item.href.includes('widget/womens-euro');
		if (isClubWorldCup || isWomensEuro) {
			setKafkaNormal({
				note: KafkaNote.TournamentWidget,
				type: 1,
				action: isClubWorldCup ? 5 : 6
			});
		}
	};

	const setKafka = () => {
		if (view === ViewEnum.galaxy) {
			setKafkaGalaxy();
		} else if (view === ViewEnum.mars) {
			setKafkaMars();
			setKafkaMarsHome();
			setKafkaMarsPinGoal();
		}
		setKafkaTournamentWidget();
	}

	const setEsportsWebKafka = () => {
		const isMobileMode = window.innerWidth < 750;
		const isGalaxy = view === ViewEnum.galaxy;
		const action = {
			'/esportsWeb': isGalaxy ? 1 : 2,
		}[item.href];

		if (action) {
			const kafkaData = {
				note: KafkaNote.EsportsWeb,
				type: isMobileMode ? 15 : 16,
				action
			}
			setKafkaNormal(kafkaData);
		}
	}

	const navigateToLink = (event: React.MouseEvent) => {
		event.stopPropagation();
		setKafka();
		uiService.controlSidebar(false);

		wcPromotionMissionService.notifyMission({
			taskType: TaskTypeEnum.ClickVirtualSports,
			sportType: Number(item.sportId),
			productCode: item.productName,
		});

		if (item.href === '/esportsWeb' && user?.EnableESportWeb && SiteConfigsData) {
			const esportsData = {
				siteId: SiteConfigsData.GetMessageID,
				umSource: t('lbl_GameUnderMaintain'),
				isApp: deepLinkSiteInfo.isApp
			};
			setEsportsWebKafka();
			openESport(esportsData, isBefore ? beforeToken : authToken, SiteConfigsData?.EnableEsportsDeeplink ?? false);
			return;
		}
		if (item.href === '/exchange') {
			openExchangeInNewWindow(isBefore, user, SiteConfigsData, t('lbl_deposit'));
			return;
		}
		if ((item.href.includes('world') || item.href.includes('favorites') || sportService.isAnyVirtualSportsProduct(Number(item.href.split('/')[2]))) && isParlayMode) {
			disableParlay();
		}
		if (item.href === '/third-party/casino-royale') {
			openCasinoRoyale({ isApp: deepLinkSiteInfo.isApp });
			return;
		}
		if (item.href === '/third-party/prediction-market') {
			openPredictionMarket();
			return;
		}
		if (item.href.includes('game-lobby')) {
			const params = new URLSearchParams(item.href.split('?')[1]);
			openCasinoRoyale({
				sportTypeId: params.get('sid'),
				betTypeId: params.get('bid'),
				category: params.get('cid'),
				isApp: deepLinkSiteInfo.isApp
			});
			return;
		}

		if (item.menuSource) {
			setCurrentPage(item.menuSource === 'virtual' ? 'lbl_VirtualSport' : 'lbl_Sports');
		}

		resetAllFilter();
		resetSwitcher();
		router.push(item.href, { locale });
	};

	const handleItemClick = (event: React.MouseEvent) => {
		let hasSendMission = false;
		Object.keys(SPORT_TYPE_TO_CLICK_TASK_TYPE).forEach((key) => {
			item?.href.split('/').forEach((segment) => {
				if (segment === key.toString()) {
					wcPromotionMissionService.notifyMission({
						taskType: TaskTypeEnum.ClickVirtualSports,
						sportType: Number(key),
						productCode: item.productName,
					});
					hasSendMission = true;
				}
			})
		})
		if (!hasSendMission) {
			wcPromotionMissionService.notifyMission({
				taskType: TaskTypeEnum.ClickVirtualSports,
				sportType: Number(item.sportId),
				productCode: item.productName,
			});
		}
		!hasSubItems || clickThenNavigate ? navigateToLink(event) : setKafka();
	};

	const getOrderedSubItems = (subItems: NavItems) => {
		if (item.href.includes('sea-games')) {
			const sportOrderList = ['1', '2', '6', '9', '12', '22', '16', '43', '21', '19', '17'];
			return subItems.sort((a, b) => {
				const aIndex = sportOrderList.indexOf(a.sportId ?? '');
				const bIndex = sportOrderList.indexOf(b.sportId ?? '');
				if (aIndex === -1 && bIndex === -1) return 0;
				if (aIndex === -1) return 1;
				if (bIndex === -1) return -1;
				return aIndex - bIndex;
			});
		}
		return subItems;
	}

	const subItems = getOrderedSubItems(item.subItems ?? [])?.map(subItem => {
		if (subItem.sportId === '999') return;
		
		return (
			<SidebarItem
				key={subItem.href}
				item={subItem}
			>
				{subItem.title}
			</SidebarItem>
		)
	}) ?? [];
	
	const toggleStreamer = (sportId?: string) => {
		if (sportId) {
			uiService.controlSidebar(false);
			router.push('/streamer', { locale });
			const width = window.innerWidth;
			const kafkaData = {
				note: (view === ViewEnum.mars) ? KafkaNote.MarsV1 : KafkaNote.GalaxyV2,
				type: (view === ViewEnum.mars) ? 5 : 13,
				action: width > 750 ? 4 : 3
			}
			wcPromotionMissionService.notifyMission({
				taskType: TaskTypeEnum.ClickSabaFeatures,
				productCode: 'streamer'
			});	
			setKafkaNormal(kafkaData);
		}
	}

	useEffect(() => {
		const subscription = modalService.onOpenGoldenDuelToolTipModal.subscribe((isOpen) => {
			if (isNeedShowGoldenDuelTooltip()) {
				setIsOpen(isOpen);
			}
		});

		const isNeedShowGoldenDuelTooltip = () => {
			return SiteConfigsData?.EnableGoldenDuel && needShowGoldenDuelTooltip;
		}

		return () => subscription.unsubscribe();
	}, []);

	return (
		<>
			<div
				className={clsx(item.className, {
					[sidebarStyle.sideFeaturesItem]: view === ViewEnum.mars && item.className === 'side-features__item',
					[sidebarStyle.sidebarItemSelected]: view === ViewEnum.mars && item.selected,
					[sidebarStyle.sideSubMenuBtn]: view === ViewEnum.mars && item.className === 'side-sub-menu__btn',
				})}
				onClick={handleItemClick}
				data-selected={item.selected}
				ref={taggedElement}
			>
				{item.icon !== '' && Maybe(item.icon).fold(
					() => null,
					(icon: string) => {
						return matchEquals(item.iconType)
							.on('general', () => <Icon name={icon} />)
							.on('sport', () => (
								<div className={clsx({ [menuIconStyle.menuIcon]: view === ViewEnum.mars })}>
									<SportIcon
										sportId={icon}
										isColorful={view === ViewEnum.galaxy ? true : item.selected}
									/>
									{view === ViewEnum.mars && item.hasLive ? (
										<div className={menuIconStyle.menuLiveIcon}>
											<FilterLiveIcon />
										</div>
									) : null}
								</div>
							))
							.on('image', () => (
								<Image
									src={icon}
									alt={item.title}
									width={24}
									height={24}
								/>
							))
							.otherwise(() => null);
					}
				)}
				<div className="text">{children}</div>
				{isOpen && item.goldTipText ? (
					<div className={clsx('tip', { [menuIconStyle.tip]: view === ViewEnum.mars })} data-open={needShowGoldenDuelTooltip}>
						<div className="scrollbar scrollbar--tip">
							<Tooltip
								message={item.goldTipText}
								className="tip__content"
								countdownTime={5000}
								showMessageDoAction={() => goldenDuelStorageService.markTooltipAsShown()}
								clearMessage={() => setNeedShowGoldenDuelTooltip(false)}
							/>
						</div>
					</div>
				) : null}
				{item.useTip ? <Tip className="tip" auto={true} /> : null}

				{item.amount && item.amount > 0 ? (
					<div className={clsx('badge badge--default', { [sidebarStyle.sideMenuBadge]: view === ViewEnum.mars })}>
						{item.amount}
					</div>
				) : null}

				{item.giftIcon ? (
					<Image 
						className="sport-event__entry-icon-desktop" 
						src={item.giftIcon} alt="" width={17} height={17}
					/>
				) : null}

				{itemNewTagConfig && !(item.sportId === '134' && item.className === 'side-menu__btn') ? (
					<NewFeatureBadge
						variant="new"
						timeRange={itemNewTagConfig}
					/>
				) : null}

				{itemPickTagConfig ? (
					<NewFeatureBadge 
						variant="pick"
						text={t('lbl_SBRPicksIcon')}
						timeRange={itemPickTagConfig}
					/>
				) : null}

				{
					SiteConfigsData?.EnableStreamerSchedule && item.hasStreamer ?
						(
							<Button
								ref={analyseStreamersRef}
								// data-active={streamerSportId.toString() === item?.sportId}
								className={sidebarStyle.streamerButton}
								variant={'icon'}
								onClick={(e) => {
									e.stopPropagation();
									toggleStreamer(item?.sportId);
								}}
							>
								<RemoteServerImage
									src="/images/live-streamer/streamer-icon.svg"
									alt=""
									width="21"
									height="21"
									isGalaxyFolder={true}
								/>
							</Button>
						) : null
				}

				{item.isNewWindow ? <NewWindowIcon className={clsx({ [menuIconStyle.rightIcon]: view === ViewEnum.mars })} /> : null}

				{item.isRedirectToProduct ? <Icon name="arrow-right" className={clsx({ [menuIconStyle.rightIcon]: view === ViewEnum.mars })} /> : null}
				{hasSubItems ? <Icon name="arrow-down" className={clsx({ [sidebarStyle.arrowDownIcon]: view === ViewEnum.mars })} /> : null}
			</div>

			{hasSubItems ? (
				<SimpleAccordionDetails className={clsx('side-sub-menu', { [sidebarStyle.sideSubMenu]: view === ViewEnum.mars })}>
					{subItems}
				</SimpleAccordionDetails>
			) : null}
		</>
	);
};
